import React, { createContext, useContext, useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';

const CartContext = createContext();

// --- Хелперы (объявлены ДО useState)
const getInitialCart = () => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem('cart');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getInitialFavorites = () => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem('favorites');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const getInitialBalance = () => {
  const today = new Date();
  const monthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;
  try {
    const saved = JSON.parse(localStorage.getItem('balance') || '{}');
    if (saved.month !== monthKey) {
      const fresh = { month: monthKey, amount: 3000 };
      localStorage.setItem('balance', JSON.stringify(fresh));
      return 3000;
    }
    return saved.amount || 3000;
  } catch {
    return 3000;
  }
};

export const CartProvider = ({ children }) => {
  const { user, authFetch } = useUser();

  const [cart, setCart] = useState(getInitialCart);
  const [favorites, setFavorites] = useState(getInitialFavorites);
  const [balance, setBalance] = useState(getInitialBalance);
  const [orders, setOrders] = useState([]);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [needsCutlery, setNeedsCutlery] = useState(false);
  const [needsBread, setNeedsBread] = useState(false);
  const [breadType, setBreadType] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);

  const [supplierDishes, setSupplierDishes] = useState([]);

  useEffect(() => {
    if (user && typeof user.balance === 'number') {
      setBalance(user.balance);
      const today = new Date();
      const monthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;
      localStorage.setItem('balance', JSON.stringify({ month: monthKey, amount: user.balance }));
    }
  }, [user, user?.balance]);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    const today = new Date();
    const monthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;
    localStorage.setItem('balance', JSON.stringify({ month: monthKey, amount: balance }));
  }, [balance]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await authFetch('/orders');
        setOrders(res || []);
      } catch (err) {
        console.error('Ошибка загрузки заказов:', err);
      }
    };

    if (user?.role === 'admin' || user?.role === 'user' || user?.role === 'customer') {
        fetchOrders();
      }
      
  }, [user, authFetch]);

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        const res = await authFetch('/supplier/dishes');
        setSupplierDishes(res || []);
      } catch (err) {
        console.error('Ошибка загрузки supplier dishes:', err);
      }
    };

    if (user?.role === 'admin') {
      fetchSupplier();
    }
  }, [user, authFetch]);

  const getNewSupplierDishCount = () => {
    const lastViewed = localStorage.getItem('adminLastViewedSupplier');
    if (!lastViewed) {
      return supplierDishes.filter(d => d.submitted).length;
    }

    const lastViewedTime = new Date(lastViewed).getTime();
    return supplierDishes.filter(d =>
      d.submitted && new Date(d.createdAt).getTime() > lastViewedTime
    ).length;
  };

  const addToCart = (dish) => {
    setCart((prev) => {
      const exists = prev.find(item => item.id === dish.id);
      if (exists) {
        return prev.map(item =>
          item.id === dish.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...dish, quantity: 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCart((prev) =>
      prev
        .map(item => item.id === id ? { ...item, quantity: item.quantity - 1 } : item)
        .filter(item => item.quantity > 0)
    );
  };

  const clearCart = () => {
    setCart([]);
    setNeedsCutlery(false);
    setNeedsBread(false);
    setBreadType(null);
    setEditingOrderId(null); // очищаем ID редактируемого заказа
  };

  const increase = (dish) => addToCart(dish);
  const decrease = (dishId) => removeFromCart(dishId);

  const toggleFavorite = (dishId) => {
    setFavorites((prev) =>
      prev.includes(dishId)
        ? prev.filter(id => id !== dishId)
        : [...prev, dishId]
    );
  };

  const deductFromBalance = (amount) => {
    setBalance(prev => {
      const newAmount = prev - amount;
      const today = new Date();
      const monthKey = `${today.getFullYear()}-${today.getMonth() + 1}`;
      localStorage.setItem('balance', JSON.stringify({ month: monthKey, amount: newAmount }));
      return newAmount;
    });
  };
  const calculateCustomTotal = () => {
    const counts = {
      первое: 0,
      второе: 0,
      салат: 0,
      напиток: 0,
    };
  
    cart.forEach(item => {
      const className = item.dishClass?.toLowerCase();
      if (className in counts) {
        counts[className] += item.quantity;
      }
    });
  
    let total = 0;
  
    const useCombo = (setPrice, combo) => {
      const canApply = combo.every(dish => counts[dish] > 0);
      if (canApply) {
        combo.forEach(dish => counts[dish]--);
        total += setPrice;
        return true;
      }
      return false;
    };
  
    // Приоритетные комбо (от большего к меньшему)
    while (
      useCombo(320, ['первое', 'второе', 'салат', 'напиток']) ||
      useCombo(270, ['первое', 'второе', 'салат']) ||
      useCombo(260, ['второе', 'салат', 'напиток']) ||
      useCombo(220, ['второе', 'салат']) ||
      useCombo(220, ['первое', 'салат'])
    );
  
    // Остатки по одиночке
    total += counts['второе'] * 170;
    total += counts['первое'] * 150;
    total += counts['салат'] * 70;
    total += counts['напиток'] * 60;
  
    return total;
  };
  

  const placeOrder = async ({ comment = '', cutlery, bread, breadTypeChoice } = {}) => {
    if (!deliveryDate) {
      alert('Дата доставки не выбрана');
      return null;
    }

    const total = calculateCustomTotal();

    const items = cart.map(item => ({
      dishId: item.id,
      name: item.name,
      imageUrl: item.imageUrl,
      quantity: item.quantity,
      price: item.price,
      dishClass: item.dishClass // добавляем класс блюда для корректного редактирования в будущем
    }));

    const orderData = {
        items,
        total,
        deliveryDate: typeof deliveryDate === 'string'
          ? deliveryDate
          : deliveryDate.toISOString().split('T')[0], // гарантируем YYYY-MM-DD
        comment,
        needsCutlery: cutlery,
        needsBread: bread
      };
      
      if (bread && breadTypeChoice) {
        orderData.breadType = breadTypeChoice;
      }

    try {
      let response;
      
      if (editingOrderId) {
        // Редактирование существующего заказа
        response = await authFetch(`/orders/${editingOrderId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderData)
        });

        // Обновляем заказ в списке заказов
        setOrders(prev => prev.map(order => 
          order.id === editingOrderId ? response : order
        ));
      } else {
        // Создание нового заказа
        const generateShortCode = () => {
          const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
          const letter = letters.charAt(Math.floor(Math.random() * letters.length));
          const number = Math.floor(100 + Math.random() * 900);
          return `${letter}-${number}`;
        };

        const newOrderData = {
          ...orderData,
          shortCode: generateShortCode(),
          createdAt: new Date().toISOString()
        };

        response = await authFetch('/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newOrderData)
        });

        setOrders(prev => [...prev, response]);
        
        // Списываем баланс только при создании нового заказа
        // При редактировании баланс обрабатывается на backend
        deductFromBalance(total);
      }

      clearCart();
      setDeliveryDate(null); // очищаем после заказа
      setNeedsCutlery(false);
      setNeedsBread(false);
      setBreadType(null);
      setEditingOrderId(null); // очищаем ID редактируемого заказа

      return response;
    } catch (err) {
      console.error('Ошибка при размещении заказа:', {
        error: err,
        message: err.message,
        editingOrderId,
        deliveryDate,
        total,
        items: items.length,
        orderData
      });
      return null;
    }
  };

    const editOrder = async (orderId) => {
        const order = orders.find(o => o.id === orderId);
        if (!order) return;

        // Проверяем, что заказ еще можно редактировать (не готов и не отменен)
        if (order.isReady || order.isCancelled) return;

        try {
            // Загружаем данные заказа в корзину
            clearCart(); // сначала очищаем
            
            // Получаем актуальные данные блюд из базы или используем сохраненные
            const dishPromises = order.items.map(async (item) => {
                // Если в заказе уже есть dishClass, используем его (новые заказы)
                if (item.dishClass) {
                    return {
                        id: item.dishId,
                        name: item.name,
                        price: item.price,
                        imageUrl: item.imageUrl,
                        dishClass: item.dishClass,
                        quantity: item.quantity
                    };
                }
                
                // Иначе получаем актуальные данные из API (старые заказы)
                try {
                    const dishData = await authFetch(`/dishes/${item.dishId}`);
                    return {
                        ...dishData,
                        quantity: item.quantity
                    };
                } catch (error) {
                    console.error(`Ошибка получения блюда ${item.dishId}:`, error);
                    // Fallback: используем данные из заказа
                    return {
                        id: item.dishId,
                        name: item.name,
                        price: item.price,
                        imageUrl: item.imageUrl,
                        dishClass: null, // будет 0 цена, но хотя бы не сломается
                        quantity: item.quantity
                    };
                }
            });

            const dishesData = await Promise.all(dishPromises);
            
            // Восстанавливаем товары из заказа с актуальными данными
            dishesData.forEach(dish => {
                for (let i = 0; i < dish.quantity; i++) {
                    addToCart({
                        id: dish.id,
                        name: dish.name,
                        price: dish.price,
                        imageUrl: dish.imageUrl,
                        dishClass: dish.dishClass // актуальный класс блюда
                    });
                }
            });

            // ✅ Устанавливаем дату доставки из заказа
            setDeliveryDate(order.deliveryDate);
            
            // ✅ Восстанавливаем дополнительные параметры заказа
            setNeedsCutlery(order.needsCutlery || false);
            setNeedsBread(order.needsBread || false);
            setBreadType(order.breadType || null);

            // ✅ Сохраняем ID редактируемого заказа
            setEditingOrderId(orderId);

            // НЕ удаляем заказ из списка - он будет обновлен, а не пересоздан

        } catch (error) {
            console.error('Ошибка при редактировании заказа:', error);
            alert('❌ Ошибка при загрузке данных заказа. Попробуйте еще раз.');
        }
    };

    const markOrderAsReady = async (orderId) => {
        try {
            await authFetch(`/orders/${orderId}/ready`, { method: 'PATCH' });
            setOrders(prev =>
                prev.map(o => o.id === orderId ? { ...o, isReady: true } : o)
            );
        } catch (err) {
            console.error('Ошибка пометки заказа как готового:', err);
        }
    };
    

  // ✅ Подсчёт новых заказов для админки, исключая отменённые
const getNewOrdersCount = () => {
  const lastViewed = localStorage.getItem('adminLastViewedOrders');
  if (!lastViewed) {
      return orders.filter(order => !order.isReady && !order.isCancelled).length;
  }

  const lastViewedTime = new Date(lastViewed).getTime();
  return orders.filter(order =>
      !order.isReady &&
      !order.isCancelled && // ✅ исключаем отменённые
      new Date(order.date).getTime() > lastViewedTime
  ).length;
};

    

    return (
        <CartContext.Provider value={{
            cart,
            favorites,
            balance,
            orders,
            addToCart,
            removeFromCart,
            clearCart,
            increase,
            decrease,
            toggleFavorite,
            deductFromBalance,
            placeOrder,
            editOrder,
            markOrderAsReady,
            getNewOrdersCount, // ✅ добавлено
            getNewSupplierDishCount,
            deliveryDate,
            setDeliveryDate,
            supplierDishes,
            needsCutlery,
            setNeedsCutlery,
            needsBread,
            setNeedsBread,
            breadType,
            setBreadType,
            calculateCustomTotal,
            editingOrderId
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
